import * as MSAL from '@azure/msal-browser';
import {microsoftTeams} from '../index';
import {IAuthService} from './types';

import OnScreenDebugger from '@utils/OnScreenDebuuger';
import db from '@utils/db';

const APP_ID = '3741153e-2490-463c-aff7-5283fb1bd1b4';

// Teams hands getAuthToken failures back as a bare string ("resourceRequiresConsent", "timeout"…)
// or a { errorCode, message } object, not an Error - which Bugsnag can only report as an opaque
// "InvalidError" with no message. Wrapping it keeps Teams' reason readable in the message.
export class TeamsAuthTokenError extends Error {
  reason: unknown;

  constructor(reason: unknown) {
    const detail = typeof reason === 'string' ? reason : JSON.stringify(reason);
    super(`Teams getAuthToken failed: ${detail}`);
    this.name = 'TeamsAuthTokenError';
    this.reason = reason;
  }
}


class TeamsAuthService implements IAuthService {
  private loginPromise: Promise<any>;

  constructor() {}

  login() {
    OnScreenDebugger.instance.log('TeamsAuthService.login();');

    if (!this.loginPromise) {
      const signInUrl: string = `${this.window.location.origin}/teams-start.html`;

      // app.initialize() rejects when the page isn't inside Teams ("No Parent window found") - the
      // deprecated initialize(callback) only reported that as an unhandled error and never called back,
      // leaving the login hanging. Now the caller (handleLogin) gets the failure.
      this.loginPromise = microsoftTeams.app.initialize().then(() => this.getUser());
    }

    return this.loginPromise;
  }

  logout() {
    return null;
  }

  isCallback() {
    return;
  }

  getUser() {
    OnScreenDebugger.instance.log('TeamsAuthService.getUser();');

    // Passes a getAuthToken() failure on to the caller (the login flow, which shows it). It used to
    // be swallowed inside a `new Promise` that then never settled: login hung silently, and the
    // rejection went unhandled.
    return this.getAuthToken().then((authToken): MSAL.AccountInfo => this.parseTokenToUser(authToken));
  }

  getAuthToken() {
    OnScreenDebugger.instance.log('TeamsAuthService.getAuthToken();');

    // claims are not used here
    return new Promise<string>((resolve, reject) => {
      microsoftTeams.authentication.getAuthToken({
        successCallback: (result) => {
          OnScreenDebugger.instance.log('TeamsAuthService.getToken(); successCallback: ' + JSON.stringify(result));

          resolve(result);
        },
        failureCallback: (reason) => {
          OnScreenDebugger.instance.log('TeamsAuthService.getToken(); failureCallback: ' + JSON.stringify(reason));
          reject(new TeamsAuthTokenError(reason));
        },
      });
    });
  }

  getToken = async (claims: string[]) => {
    OnScreenDebugger.instance.log('TeamsAuthService.getToken();');
    let authToken: string = await this.getAuthToken();
    OnScreenDebugger.instance.log('authToken: ' + authToken);
    let graphToken = await db.get_token(APP_ID, authToken);
    OnScreenDebugger.instance.log('graphToken: ' + JSON.stringify(graphToken));

    return graphToken.access_token;
  };

  ensureLoginHint() {
    //OnScreenDebugger.instance.log('TeamsAuthService.ensureLoginHint();');

    return new Promise((resolve) => {
      resolve(null);
      microsoftTeams.getContext((context) => {
        const scopes = encodeURIComponent('email profile User.ReadBasic.All, User.Read.All, Group.Read.All, Directory.Read.All');
        resolve(null);
      });
    });
  }

  handleLoginRedirect() {
    return null;
  }

  private parseTokenToUser(token: string): MSAL.AccountInfo {
    //OnScreenDebugger.instance.log('TeamsAuthService.parseTokenToUser();');
    //OnScreenDebugger.instance.log('token : ' + token);

    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const parsedToken = JSON.parse(this.window.atob(base64));
    // const nameParts = parsedToken.name.split(' ');

    //OnScreenDebugger.instance.log('parsedToken : ' + JSON.stringify(parsedToken));

    const parsed = {
      homeAccountId: parsedToken.oid + '.' + parsedToken.tid,
      localAccountId: parsedToken.oid,
      environment: 'login.windows.net',
      tenantId: parsedToken.tid,
      name: parsedToken.name,
      username: parsedToken.upn,
      idTokenClaims: parsedToken,
    };

    //OnScreenDebugger.instance.log('parsed : ' + JSON.stringify(parsed));

    return parsed;
  }

  get config() {
    return {
      clientId: APP_ID,
      redirectUri: this.window.location.href + '/',
      cacheLocation: 'localStorage',
      navigateToLoginRequestUrl: false,
    } as any;
  }

  private get window() {
    return window || global;
  }
}

export default TeamsAuthService;
